import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { PERMISSIONS_EVENT, isSystemUser, loadMyPermissions, readPermissions } from "../../helpers/permissions";
import { activeGroupKey, buildSidebar, isActiveRoute, pageTitle } from "./sidebarRules";
import useSidebarLayout from "./useSidebarLayout";
import "./sidebar.css";

const Item = ({ item, onNavigate }) => (
  <li>
    <NavLink
      to={item.route}
      className="sb-link"
      activeClassName="is-active"
      isActive={(_, location) => isActiveRoute(item.route, location.pathname)}
      title={item.label}
      onClick={onNavigate}
    >
      <i className={`sb-link__icon ${item.icon}`} aria-hidden="true" />
      <span className="sb-link__text">{item.label}</span>
    </NavLink>
  </li>
);

/**
 * The website's sidebar, built from App Menu (screens: name, group, order, icon, route) and the user's access rights; only
 * the screens the user may view are shown. The current page is highlighted and its group opens; one group open at a time.
 * Collapsed (icons only, groups open as a flyout) on wide screens, a slide-in panel on phones (useSidebarLayout).
 */
export default function Sidebar() {
  const { pathname } = useLocation();
  const [permissions, setPermissions] = useState(readPermissions);
  const [openKey, setOpenKey] = useState("");
  const layout = useSidebarLayout();

  const entries = useMemo(() => buildSidebar(permissions, { systemUser: isSystemUser() }), [permissions]);

  // Rebuild when the rights are (re)loaded; an older cache without the sidebar groups is refreshed once.
  useEffect(() => {
    const reload = () => setPermissions(readPermissions());
    window.addEventListener(PERMISSIONS_EVENT, reload);
    const cached = readPermissions();
    if (cached && !cached.groups) loadMyPermissions();
    return () => window.removeEventListener(PERMISSIONS_EVENT, reload);
  }, []);

  // The current page's group opens (not as a flyout when collapsed); the page title follows the menu name.
  useEffect(() => {
    const key = activeGroupKey(entries, pathname);
    setOpenKey(layout.collapsed ? "" : key);
    const title = pageTitle(entries, pathname);
    const label = document.querySelector("#lblPageTitle");
    if (title && label) label.textContent = title;
  }, [entries, pathname, layout.collapsed]);

  // Collapsed: a click outside the sidebar closes the open flyout.
  useEffect(() => {
    if (!layout.collapsed || !openKey) return undefined;
    const onClick = (e) => {
      if (!e.target.closest(".sb")) setOpenKey("");
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [layout.collapsed, openKey]);

  const toggleGroup = useCallback((key) => setOpenKey((k) => (k === key ? "" : key)), []);
  const onNavigate = useCallback(() => {
    if (layout.collapsed) setOpenKey(""); // close the flyout
    layout.closeMobile();
  }, [layout]);

  return (
    <>
      <div className="main-sidebar sb">
        <aside id="sidebar-wrapper" className="sb__inner" aria-label="Main menu">
          <div className="sidebar-brand sb__brand">
            <NavLink to="/dashboard-overview" onClick={onNavigate}>
              <img src="../assets/img/logo_akasa_air_inside.png" alt="Akasa Air" width="180" />
            </NavLink>
          </div>
          <div className="sb__brand-sm">
            <NavLink to="/dashboard-overview" onClick={onNavigate}>
              <img src="../assets/img/icon_akasa_air.png" alt="Akasa Air" width="24" height="24" />
            </NavLink>
          </div>
          <nav className="sb__nav">
            <ul className="sb-menu">
              {entries.map((e) => {
                if (e.type === "item") return <Item key={e.key} item={e} onNavigate={onNavigate} />;
                const open = openKey === e.key;
                const hasActive = e.items.some((i) => isActiveRoute(i.route, pathname));
                return (
                  <li key={e.key} className={`sb-group${open ? " is-open" : ""}${hasActive ? " has-active" : ""}`}>
                    <button type="button" className="sb-link sb-group__head" aria-expanded={open} title={e.label} onClick={() => toggleGroup(e.key)}>
                      <i className={`sb-link__icon ${e.icon}`} aria-hidden="true" />
                      <span className="sb-link__text">{e.label}</span>
                      <i className="fas fa-chevron-right sb-group__chevron" aria-hidden="true" />
                    </button>
                    <ul className="sb-sub" aria-label={e.label}>
                      <li className="sb-sub__title">{e.label}</li>
                      {e.items.map((i) => (
                        <Item key={i.key} item={i} onNavigate={onNavigate} />
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="sb__powered">
            <span>Powered by</span>
            <a href="http://www.plexitech.com/">
              <img src="../assets/img/logo-plexitech2.png" alt="PlexiTech" />
            </a>
          </div>
        </aside>
      </div>
      {layout.mobileOpen && <button type="button" className="sb-backdrop" aria-label="Close menu" onClick={layout.closeMobile} />}
    </>
  );
}
